/**
 * Getting Alt+Tab delivered to the page at all.
 *
 * Alt+Tab belongs to the operating system. Windows takes it before any browser
 * sees it, so a keydown for it never reaches this code — the same wall
 * `WindowLayer` already runs into with Alt+F4, and the reason the switcher is
 * also bound to Alt+` (see `AltTab.tsx`), which nothing has a claim on.
 *
 * There is exactly one way through it, and it is a real one: the Keyboard Lock
 * API. A fullscreen page may ask for the system keys, and Alt+Tab is precisely
 * the case it was specified for. So the lock is taken whenever the document
 * goes fullscreen and dropped when it leaves, which means F11 turns Alt+Tab
 * from the OS's key into this desktop's key and back.
 *
 * Chromium-only, secure contexts only, and it fails quietly everywhere else,
 * which is the correct outcome: the other binding still works.
 */

interface KeyboardLock {
  lock(keyCodes?: string[]): Promise<void>;
  unlock(): void;
}

function keyboardLock(): KeyboardLock | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const keyboard = (navigator as Navigator & { keyboard?: KeyboardLock }).keyboard;
  return typeof keyboard?.lock === 'function' ? keyboard : undefined;
}

/**
 * Hold the lock for as long as the page is fullscreen. Returns the teardown,
 * in the shape every other listener installer in the shell uses.
 */
export function installKeyboardLock(): () => void {
  const keyboard = keyboardLock();
  if (keyboard === undefined || typeof document === 'undefined') return () => {};

  const release = () => {
    try {
      keyboard.unlock();
    } catch {
      // Nothing was locked, or the page is already gone.
    }
  };

  const sync = () => {
    if (document.fullscreenElement) {
      // 'Tab' is the code that covers Alt+Tab. A rejection means the browser
      // declined, which is not something the desktop needs to know about.
      keyboard.lock(['Tab']).catch(() => {});
    } else {
      release();
    }
  };

  document.addEventListener('fullscreenchange', sync);
  sync();

  return () => {
    document.removeEventListener('fullscreenchange', sync);
    release();
  };
}
